import { useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, Loader2, Share2, Unlink, Wifi, WifiOff } from "lucide-react";
import { useTheme } from "next-themes";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { AdminSocialSubNav } from "@/components/admin/AdminSocialSubNav";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

interface Connection {
  id: string;
  platform: string;
  handle: string | null;
  connected_at: string | null;
  expires_at: string | null;
  is_active: boolean;
}

const PLATFORMS = [
  { value: "instagram", emoji: "📸", label: "Instagram", desc: "Gönderiler, Hikayeler ve Reels yayımlama" },
  { value: "tiktok", emoji: "🎵", label: "TikTok", desc: "Video yükleme ve hesap yönetimi" },
  { value: "facebook", emoji: "📘", label: "Facebook", desc: "Sayfa yönetimi ve gönderi planlama" },
  { value: "twitter", emoji: "🐦", label: "Twitter / X", desc: "Tweet ve yanıt yönetimi" },
  { value: "youtube", emoji: "▶️", label: "YouTube", desc: "Video yükleme ve kanal yönetimi" },
];

export default function AdminSocialConnections() {
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const authenticated = !!sessionQuery.data?.authenticated;
  const configured = !!sessionQuery.data?.configured;
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";

  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionPlatform, setActionPlatform] = useState<string | null>(null);
  const [instagramStatus, setInstagramStatus] = useState<{ connected: boolean; account_id?: string } | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!authenticated) return;
    adminFetch("/api/admin/social/connections")
      .then((r) => r.json())
      .then((json) => {
        if (json.ok) setConnections(json.data ?? []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    fetch("/api/social/instagram/status")
      .then((r) => r.json())
      .then((json) => {
        if (json && typeof json.connected === "boolean") setInstagramStatus(json);
      })
      .catch(() => {});
  }, [authenticated]);

  const connMap = Object.fromEntries(connections.filter((c) => c.is_active).map((c) => [c.platform, c]));
  if (instagramStatus?.connected && !connMap["instagram"]) {
    connMap["instagram"] = {
      id: "instagram-direct",
      platform: "instagram",
      handle: instagramStatus.account_id ?? "Bağlı",
      connected_at: null,
      expires_at: null,
      is_active: true,
    };
  }

  async function handleConnect(platform: string) {
    setActionPlatform(platform);
    try {
      const r = await adminFetch(`/api/admin/social/connections/${platform}/connect`, { method: "POST" });
      const json = await r.json();
      if (json.ok && json.oauth_url) {
        window.open(json.oauth_url, "_blank");
        toast({
          title: "OAuth penceresi açıldı",
          description: "Yetkilendirme tamamlandıktan sonra sayfayı yenile.",
        });
      } else {
        toast({ title: "Hata", description: json.error, variant: "destructive" });
      }
    } finally {
      setActionPlatform(null);
    }
  }

  async function handleDisconnect(platform: string) {
    setActionPlatform(platform);
    try {
      const r = await adminFetch(`/api/admin/social/connections/${platform}`, { method: "DELETE" });
      const json = await r.json();
      if (json.ok) {
        setConnections((prev) => prev.map((c) => (c.platform === platform ? { ...c, is_active: false } : c)));
        toast({ title: `${platform} bağlantısı kesildi` });
      } else {
        toast({ title: "Hata", description: json.error, variant: "destructive" });
      }
    } finally {
      setActionPlatform(null);
    }
  }

  if (sessionQuery.isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="flex items-center gap-2">
                <Share2 className="h-5 w-5 text-slate-500 dark:text-slate-300" />
                <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                  Sosyal Medya
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <AdminSocialSubNav />

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
            {loading ? (
              <div className="flex h-64 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                    Platform Bağlantıları
                  </div>
                  <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Sosyal medya hesaplarını bağla ve yönet
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {PLATFORMS.map((p) => {
                    const conn = connMap[p.value];
                    const isConnected = Boolean(conn);
                    const isLoading = actionPlatform === p.value;

                    return (
                      <div
                        key={p.value}
                        className={`rounded-2xl border p-5 ${
                          isConnected
                            ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-700/40 dark:bg-emerald-950/20"
                            : "border-slate-200 bg-white dark:border-slate-700/50 dark:bg-slate-800/40"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-xl dark:bg-slate-900">
                              {p.emoji}
                            </div>
                            <div>
                              <div className="font-medium text-slate-900 dark:text-slate-100">{p.label}</div>
                              <div className="mt-0.5 text-xs text-muted-foreground">{p.desc}</div>
                            </div>
                          </div>
                          {isConnected ? (
                            <Wifi className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <WifiOff className="h-4 w-4 text-slate-400 dark:text-slate-600" />
                          )}
                        </div>

                        <div className="mt-4">
                          {isConnected ? (
                            <>
                              <div className="mb-3 flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-300">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                <span className="font-medium">{conn.handle ?? "Bağlı"}</span>
                              </div>
                              {conn.connected_at && (
                                <p className="mb-3 text-[11px] text-muted-foreground">
                                  Bağlandı: {new Date(conn.connected_at).toLocaleDateString("tr-TR")}
                                </p>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-700/50 dark:text-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
                                disabled={isLoading}
                                onClick={() => handleDisconnect(p.value)}
                              >
                                {isLoading ? (
                                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Unlink className="mr-2 h-3.5 w-3.5" />
                                )}
                                Bağlantıyı Kes
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              disabled={isLoading}
                              onClick={() => handleConnect(p.value)}
                            >
                              {isLoading ? (
                                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <ExternalLink className="mr-2 h-3.5 w-3.5" />
                              )}
                              Bağlan
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-500 dark:border-slate-700/50 dark:bg-slate-800/40 dark:text-slate-400">
                  <strong className="text-slate-700 dark:text-slate-300">OAuth Notu:</strong> Platform bağlantıları
                  için ilgili geliştirici uygulamasının oluşturulmuş ve ortam değişkenlerinin (INSTAGRAM_APP_ID,
                  TIKTOK_CLIENT_KEY vb.) ayarlı olması gerekir. Yetkilendirme tamamlandıktan sonra callback'ten dönen
                  token otomatik kaydedilir.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
